import type { LenderChangeOrigin } from '../types/reserveSplitTypes';
import { BALLOON, FINANCE, LEASE, RESIDUAL_TYPE } from '@makemydeal/dr-platform-types';
import type {
    CalculationEndpointType,
    CoBuyer,
    CommonConsumer,
    CreditScoreSource,
    GenericObject,
    GridHeadersData,
    ICreditDecisionLender,
    IPaymentOverride,
    IShopper,
    ItemizedDiscount,
    IVehicle,
    OfferType,
    OutsideLienHolder,
    PartialRestoreRecord,
    paymentServicesTypes,
    ResidualValueType
} from '@makemydeal/dr-platform-types';
import type {
    PaymentGridCarriedOverride,
    PaymentGridRestoredInputs,
    PaymentReduxAction,
    StandardsPendingLocationChange
} from '../types/paymentTypes';
import type { PriorLeaseBalance, ResetOptions, ResidualValues } from '../types/commonTypes';
import { combineCommonConsumerContactInfo, getFirstFiveDigitsPostalCode } from '../utils/buyerUtils';
import { initialState } from '../reducers/initialState';
import { addPaymentGridMeta } from '../utils/paymentGridUtils';
import * as types from '../types/actionTypes';
import {
    CAPTURE_OVERRIDES_FALLBACK_SNAPSHOT,
    CHANGE_VEHICLE_UPDATE_OVERRIDES,
    CLEAR_OVERRIDES_FALLBACK_SNAPSHOT,
    FULL_RESET_OVERRIDES,
    HONOR_LEASE_MILEAGE_CHANGE,
    LOCATION_RESET_OVERRIDES,
    PARTIAL_RESET_OVERRIDES,
    PARTIAL_RESTORE_OVERRIDES,
    RESTORE_OVERRIDES_FOR_FALLBACK,
    SELECTED_CREDIT_PARTIAL_RESET_OVERRIDES,
    SET_APPLICATION_NAME
} from '../types/actionTypes';
import type { InitOfferCreditTiersActionPayload } from './payloadTypes';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { PAYMENT_REQUEST_OVERRIDE_NODE } from '../constants';
import type { DropOverrides } from '../constants';

export type SuppressOptions = {
    suppressAnalytics?: boolean;
    suppressPayment?: boolean;
};

export type SelectTermMetaOptions = {
    paymentRequestOverride?: {
        balloon?: {
            termMonths: number;
            termList: number[];
            downPayment: number;
        };
    };
    [key: string]: unknown;
};

/**
 * @param payment is used to determine if the action should trigger a payment call
 * @param offerType you can pass specific offer type as metadata in SELECT_TERM action
 * @param paymentGridSV is used to determine if term selected from Payment Grid in Sales View
 * @param meta contains payment request overrides and other metadata
 */
export type SelectTermOptions = SuppressOptions & {
    payment?: boolean;
    offerType?: OfferType;
    paymentGridSV?: boolean;
    hasTermMonthsChanged?: boolean;
    meta?: SelectTermMetaOptions | GenericObject<unknown>;
};

export type UpdateOfferTypeOptions = SuppressOptions & {
    payment?: boolean;
};

export type vehicleInfoPayload = {
    retailPrice?: number;
    sellingPrice?: number;
    vehicle?: IVehicle;
};

export type UpdateTermGridOptions = {
    nonSelectedTermHonoringSV?: boolean;
    isMainPayment?: boolean;
    additionalPropsForSVPG?: any[];
    shouldSkipPaymentCall?: boolean;
};

/**
 * The two per-call engine flags, on their own type rather than the shared options: passing them to a creator that does
 * not read them would compile and then be silently dropped. Balloon deliberately stays on `UpdateTermGridOptions`.
 */
export type UpdateGridRowEngineOptions = UpdateTermGridOptions & {
    /** Forces the engine for this call alone, so a column with no program prices through user-program-quotes while the deal's endpoint stays as is. */
    forceUseGenericCalcs?: boolean;
    /** Set only on the two halves of a split row, whose responses cannot be trusted alone. A single-call row does not set it, so it can still trigger a genuine fallback. */
    isGridRowSplitPart?: boolean;
    gridColumnIndices?: number[];
    gridRowIndices?: number[];
    columnSellRate?: number;
    /**
     * The residual THIS column owns, for a term more than one column holds. Declared here because the creators build
     * their meta from named options: `columnScopedMeta` returns a `GenericObject`, so spreading it skipped the excess
     * property check and this key was accepted and then silently dropped - a duplicated Lease column's own residual
     * never reached the request.
     */
    columnResidualInfo?: GenericObject;
    ignoreTermSellRateOverride?: boolean;
    ignoreTermResidualOverride?: boolean;
    restoredColumnPrograms?: Record<number, GenericObject>;
};

// Update Offer type
export function updatedOfferType(offerType: OfferType, options: UpdateOfferTypeOptions = {}) {
    const payment = options.payment ?? true;
    const action: AnyFSA = {
        type: types.UPDATED_OFFER_TYPE,
        payload: offerType,
        meta: {
            middleware: {
                analytics: {
                    eventName: 'DR_PAYMENT_TYPE_CHANGED'
                },
                payment
            }
        }
    };
    if (options?.suppressAnalytics) {
        delete action.meta.middleware.analytics;
    }
    return action;
}

export function fetchOfferTypesData(offerType: OfferType) {
    return {
        type: types.FETCH_OFFER_TYPES_DATA,
        meta: {
            middleware: {
                payment: true,
                offerType
            }
        }
    };
}

export function updatedCounterOfferMessage(message: string) {
    return {
        type: types.UPDATED_COUNTEROFFER_MESSAGE,
        payload: `DEALER OFFER NOT ACCEPTED - ${message}`,
        meta: {
            middleware: {
                payment: false
            }
        }
    };
}

export function selectedTerm(termId: string | undefined, options: SelectTermOptions = {}) {
    const payment = options.payment ?? true;
    const { hasTermMonthsChanged } = options;

    const action: AnyFSA = {
        type: types.SELECTED_TERM,
        payload: termId,
        meta: {
            middleware: {
                analytics: {
                    eventName: 'DR_PAYMENT_TERM_CHANGED'
                },
                payment,
                offerType: options.offerType,
                paymentGridSV: options.paymentGridSV ?? false,
                ...(hasTermMonthsChanged !== undefined && { hasTermMonthsChanged })
            },
            ...options.meta
        }
    };

    if (options?.suppressAnalytics) {
        delete action.meta.middleware.analytics;
    }

    return action;
}

export function fetchGridData(termList: number[], downPayment: number, offerType: OfferType) {
    const action: PaymentReduxAction = {
        type: types.FETCH_TERMS_GRID_DATA,
        meta: {
            middleware: {
                payment: true,
                offerType
            },
            [PAYMENT_REQUEST_OVERRIDE_NODE]: {
                [offerType]: {
                    termList,
                    downPayment
                }
            }
        }
    };

    return action;
}

export function updateGridSnapshot(gridData: GridHeadersData, offerType: OfferType) {
    const action: PaymentReduxAction = {
        type: types.UPDATE_GRID_HEADERS_SNAPSHOT,
        payload: { gridData, offerType },
        meta: {
            middleware: {
                payment: false
            }
        }
    };

    return action;
}
export function updateFinanceTermsGrid(termList: number[], downPayment: number, options: UpdateGridRowEngineOptions = {}) {
    const action: PaymentReduxAction = {
        type: types.UPDATE_FINANCE_TERMS_GRID,
        meta: {
            middleware: {
                analytics: {
                    eventName: 'UPDATE_FINANCE_TERMS_GRID'
                },
                payment: !options.shouldSkipPaymentCall,
                ...(options.nonSelectedTermHonoringSV && { nonSelectedTermHonoringSV: options.nonSelectedTermHonoringSV }),
                ...(options.additionalPropsForSVPG && { additionalPropsForSVPG: options.additionalPropsForSVPG })
            },
            ...(typeof options.forceUseGenericCalcs === 'boolean' && {
                forceUseGenericCalcs: options.forceUseGenericCalcs
            }),
            ...(options.isGridRowSplitPart && { isGridRowSplitPart: true }),
            ...(options.gridColumnIndices?.length && { gridColumnIndices: options.gridColumnIndices }),
            ...(options.gridRowIndices?.length && { gridRowIndices: options.gridRowIndices }),
            ...(options.columnSellRate != null && { columnSellRate: options.columnSellRate }),
            ...(options.ignoreTermSellRateOverride && { ignoreTermSellRateOverride: true }),
            ...(options.restoredColumnPrograms && { restoredColumnPrograms: options.restoredColumnPrograms }),
            [PAYMENT_REQUEST_OVERRIDE_NODE]: {
                finance: {
                    termList,
                    downPayment
                }
            }
        }
    };

    return addPaymentGridMeta(action, { isMainPayment: options.isMainPayment || false });
}

export function updateBalloonTermsGrid(termList: number[], downPayment: number, options: UpdateTermGridOptions = {}) {
    const action: PaymentReduxAction = {
        type: types.UPDATE_BALLOON_TERMS_GRID,
        meta: {
            middleware: {
                analytics: {
                    eventName: 'UPDATE_BALLOON_TERMS_GRID'
                },
                payment: !options.shouldSkipPaymentCall
            },
            [PAYMENT_REQUEST_OVERRIDE_NODE]: {
                balloon: {
                    termList,
                    downPayment
                }
            }
        }
    };

    return addPaymentGridMeta(action, { isMainPayment: options.isMainPayment || false });
}

export function updateLeaseTermsGrid(termList: number[], downPayment: number, options: UpdateGridRowEngineOptions = {}) {
    const action: PaymentReduxAction = {
        type: types.UPDATE_LEASE_TERMS_GRID,
        meta: {
            middleware: {
                analytics: {
                    eventName: 'UPDATE_LEASE_TERMS_GRID'
                },
                // US2085313: honoured for Lease now, as Finance already does. It used to be hardcoded true
                // because MV painted Lease cells from `terms.byId` alone, so skipping the calls left an empty
                // grid. Restoring the stored grid into that node is what removes the objection — the cells are
                // there to paint, and a row that already holds every quote has nothing to ask for.
                payment: !options.shouldSkipPaymentCall,
                ...(options.nonSelectedTermHonoringSV && { nonSelectedTermHonoringSV: options.nonSelectedTermHonoringSV })
            },
            // The same two per-call flags Finance carries, both omitted unless the caller sets them, so every existing
            // caller produces the identical action it did before.
            ...(typeof options.forceUseGenericCalcs === 'boolean' && {
                forceUseGenericCalcs: options.forceUseGenericCalcs
            }),
            ...(options.isGridRowSplitPart && { isGridRowSplitPart: true }),
            ...(options.gridColumnIndices?.length && { gridColumnIndices: options.gridColumnIndices }),
            ...(options.gridRowIndices?.length && { gridRowIndices: options.gridRowIndices }),
            ...(options.columnSellRate != null && { columnSellRate: options.columnSellRate }),
            ...(options.columnResidualInfo != null && { columnResidualInfo: options.columnResidualInfo }),
            ...(options.ignoreTermSellRateOverride && { ignoreTermSellRateOverride: true }),
            ...(options.ignoreTermResidualOverride && { ignoreTermResidualOverride: true }),
            ...(options.restoredColumnPrograms && { restoredColumnPrograms: options.restoredColumnPrograms }),
            [PAYMENT_REQUEST_OVERRIDE_NODE]: {
                lease: {
                    termList,
                    downPayment
                }
            }
        }
    };

    return addPaymentGridMeta(action, { isMainPayment: options.isMainPayment || false });
}

export function updatePaymentGridRow(
    offerType: OfferType,
    termsList: number[],
    downPayment: number,
    options: UpdateGridRowEngineOptions = {}
) {
    if (offerType === FINANCE) return updateFinanceTermsGrid(termsList, downPayment, options);
    // Lease needs the options through, or a row can only ever be priced on the deal's own engine.
    if (offerType === LEASE) return updateLeaseTermsGrid(termsList, downPayment, options);
    /**
     * Balloon receives `shouldSkipPaymentCall` and nothing else. It was arg-less because threading the whole
     * options object would change how a Shopper-Platform restore prices its rows — that caution was about
     * `forceUseGenericCalcs` and `isGridRowSplitPart`, which still do not reach it. The skip flag its own
     * creator already reads has to, or a balloon grid re-prices every restored row on open while Finance and
     * Lease do not.
     */
    if (offerType === BALLOON)
        return updateBalloonTermsGrid(termsList, downPayment, { shouldSkipPaymentCall: options.shouldSkipPaymentCall });
    throw new Error('Unsupported offer type provided!');
}

export function initializePaymentGridTerms(offerType: OfferType, terms: number[]) {
    return {
        type: types.INITIALIZE_PAYMENT_GRID_TERMS,
        payload: {
            offerType,
            terms
        }
    };
}

export function initializePaymentGridCashdown(offerType: OfferType, cashDown: number[] = []) {
    return {
        type: types.INITIALIZE_PAYMENT_GRID_CASHDOWN,
        payload: {
            offerType,
            cashDown
        }
    };
}

export function updatePaymentGridTerm(offerType: OfferType, key: any, val: any, userIntent?: string) {
    return {
        type: types.UPDATE_PAYMENT_GRID_TERM,
        payload: {
            offerType,
            key,
            val
        },
        ...(userIntent && { meta: { userIntent } })
    };
}

export function updatePaymentGridCashdown(offerType: OfferType, key: any, val: any, userIntent?: string) {
    return {
        type: types.UPDATE_PAYMENT_GRID_CASH_DOWN,
        payload: {
            offerType,
            key,
            val
        },
        ...(userIntent && { meta: { userIntent } })
    };
}

/**
 * Both off by default, passed only on the redesign path. `skipProgramOverride` searches across programs instead of
 * pinning the one in state, which would defeat discovery; `dropOverrides` sends no overrides, as a typed rate cannot
 * steer a server-side search. The down payment is still carried, deliberately.
 */
export function loadPaymentGridLenderPrograms(
    termList: number[],
    downPayment: number,
    offerType: OfferType,
    options: { skipProgramOverride?: boolean; dropOverrides?: boolean; forceProgramQuotes?: boolean } = {}
) {
    const action: PaymentReduxAction = {
        type: types.LOAD_PAYMENT_GRID_LENDER_PROGRAMS,
        meta: {
            ...(options.dropOverrides && { dropOverridesForDiscovery: true }),
            ...(options.forceProgramQuotes && { forceUseGenericCalcs: false }),
            middleware: {
                analytics: {
                    eventName: 'LOAD_PAYMENT_GRID_LENDER_PROGRAMS'
                },
                payment: true,
                ...(options.skipProgramOverride && { skipProgramOverride: true })
            },
            [PAYMENT_REQUEST_OVERRIDE_NODE]: {
                [offerType]: {
                    termList,
                    downPayment
                }
            }
        }
    };

    return action;
}

export function loadPaymentGridTermLenderProgram(
    key: number,
    term: number,
    downPayment: number,
    offerType: OfferType,
    options: { skipProgramOverride?: boolean; dropOverrides?: boolean; forceProgramQuotes?: boolean } = {}
) {
    const action: PaymentReduxAction = {
        type: types.LOAD_PAYMENT_GRID_TERM_LENDER_PROGRAM,
        payload: {
            key
        },
        meta: {
            ...(options.dropOverrides && { dropOverridesForDiscovery: true }),
            ...(options.forceProgramQuotes && { forceUseGenericCalcs: false }),
            middleware: {
                analytics: {
                    eventName: 'LOAD_PAYMENT_GRID_TERM_LENDER_PROGRAM'
                },
                payment: true,
                ...(options.skipProgramOverride && { skipProgramOverride: true })
            },
            [PAYMENT_REQUEST_OVERRIDE_NODE]: {
                [offerType]: {
                    termList: [term],
                    downPayment
                }
            }
        }
    };

    return action;
}

export function loadPaymentGridData(preInitializedPaymentGrid?: GenericObject, offerType?: OfferType) {
    return {
        type: types.LOAD_PAYMENT_GRID_DATA,
        ...((preInitializedPaymentGrid || offerType) && {
            meta: {
                ...(preInitializedPaymentGrid && { preInitializedPaymentGrid }),
                // Carries the target offer type so the payment-grid middleware can populate an
                // offer type other than the deal's current one (Alt Deal Structure lease populate).
                ...(offerType && { middleware: { offerType } })
            }
        })
    };
}

export function loadPaymentGridTermData(
    key: any,
    termMonth: number,
    offerType?: OfferType,
    options: {
        /** Carried from the response, not re-read from `programInfoByTerm`, which is merge-only and would report a program that no longer qualifies. */
        hasDiscoveredProgram?: boolean;
        gridColumnIndex?: number;
    } = {}
) {
    const hasMeta = Boolean(offerType) || typeof options.hasDiscoveredProgram === 'boolean' || options.gridColumnIndex != null;

    return {
        type: types.LOAD_PAYMENT_GRID_TERM_DATA,
        payload: {
            key,
            termMonth
        },
        // Carries the target offer type so the column reload can target an offer type other than the
        // deal's current one (Alt Deal Structure lease single-term edit).
        ...(hasMeta && {
            meta: {
                ...(offerType && { middleware: { offerType } }),
                ...(typeof options.hasDiscoveredProgram === 'boolean' && {
                    hasDiscoveredProgram: options.hasDiscoveredProgram
                }),
                ...(options.gridColumnIndex != null && { gridColumnIndex: options.gridColumnIndex })
            }
        })
    };
}

/** Stores (or clears, with `undefined`) a sell rate typed into a column with no term yet, keyed by column index. No payment: nothing to price. */
export function setPaymentGridPendingSellRate(offerType: OfferType, columnIndex: number, sellRate: number | undefined) {
    return {
        type: types.SET_PAYMENT_GRID_PENDING_SELL_RATE,
        payload: {
            offerType,
            columnIndex,
            sellRate
        }
    };
}

/**
 * Holds the rate pair a column carried into a term change, keyed by the INCOMING term, for its user-program-quotes
 * fallback. Merged; a `null` entry releases a hold. Kept out of `overridesByTerm`, which the in-flight PQ call reads.
 */
export function setPaymentGridFallbackRates(
    offerType: OfferType,
    ratesByTerm: Record<number, { sellRate?: number; buyRate?: number; rateType?: string; residual?: GenericObject } | null>
) {
    return {
        type: types.SET_PAYMENT_GRID_FALLBACK_RATES,
        payload: {
            offerType,
            ratesByTerm
        }
    };
}

/**
 * Records that the user typed a sell rate into this grid column, in this grid session. Kept apart from the override
 * itself, which has many writers and so cannot say who wrote it - and which deliberately outlives the grid.
 */
export function setPaymentGridColumnCells(offerType: OfferType, cells: Record<string, GenericObject>) {
    return {
        type: types.SET_PAYMENT_GRID_COLUMN_CELLS,
        payload: { offerType, cells }
    };
}

export function restorePaymentGridRowQuotes(offerType: OfferType, quotedTermsByRow: Record<number, number[]>) {
    return {
        type: types.RESTORE_PAYMENT_GRID_ROW_QUOTES,
        payload: { offerType, quotedTermsByRow }
    };
}

export function setPaymentGridDecidedColumns(offerType: OfferType, decidedColumns: Record<number, string>) {
    return {
        type: types.SET_PAYMENT_GRID_DECIDED_COLUMNS,
        payload: { offerType, decidedColumns }
    };
}

export function setPaymentGridColumnOverride(
    offerType: OfferType,
    columnIndex: number,
    override: { sellRate?: number; rateType?: string; residualInfo?: GenericObject }
) {
    return {
        type: types.SET_PAYMENT_GRID_COLUMN_OVERRIDE,
        payload: { offerType, columnIndex, override }
    };
}

export function setPaymentGridRestoredInputs(offerType: OfferType, restoredInputs: Record<number, PaymentGridRestoredInputs>) {
    return {
        type: types.SET_PAYMENT_GRID_RESTORED_INPUTS,
        payload: { offerType, restoredInputs }
    };
}

export function setPaymentGridCarriedOverride(
    offerType: OfferType,
    columnIndex: number,
    carriedOverride: PaymentGridCarriedOverride | undefined
) {
    return {
        type: types.SET_PAYMENT_GRID_CARRIED_OVERRIDE,
        payload: { offerType, columnIndex, carriedOverride }
    };
}

export function adoptPaymentGridCell(
    offerType: OfferType,
    termId: string,
    cell: GenericObject | undefined,
    columnResidualInfo?: GenericObject,
    columnRate?: { sellRate?: number; rateType?: string },
    columnIndex?: number
) {
    return {
        type: types.ADOPT_PAYMENT_GRID_CELL,
        payload: { offerType, termId, cell, columnResidualInfo, columnRate, columnIndex }
    };
}

export function setPaymentGridSelectedRow(offerType: OfferType, rowIndex: number | undefined) {
    return {
        type: types.SET_PAYMENT_GRID_ACTIVE_CELL,
        payload: { offerType, axis: 'row' as const, index: rowIndex }
    };
}

export function setPaymentGridSelectedColumn(offerType: OfferType, columnIndex: number | undefined) {
    return {
        type: types.SET_PAYMENT_GRID_ACTIVE_CELL,
        payload: { offerType, axis: 'column' as const, index: columnIndex }
    };
}

export function setPaymentGridUserPricedTerm(offerType: OfferType, termMonths: number) {
    return {
        type: types.SET_PAYMENT_GRID_USER_PRICED_TERM,
        payload: {
            offerType,
            termMonths
        }
    };
}

export function setPaymentGridWrittenTerm(
    offerType: OfferType,
    termMonths: number,
    half: 'rate' | 'residual',
    { clear = false }: { clear?: boolean } = {}
) {
    return {
        type: types.SET_PAYMENT_GRID_WRITTEN_TERM,
        payload: {
            offerType,
            termMonths,
            half,
            ...(clear && { clear: true })
        }
    };
}

/**
 * Records the engine each column resolved to, by term months. Merged by default.
 *
 * `replace` is for the whole-grid resolution pass, which is the single writer of every column's verdict for its
 * build: merging there let an `Unavailable` written by a superseded build outlive it, and since only
 * `RESET_PAYMENT_GRID` ever removes an entry, that column then painted blank for the life of the grid. The
 * per-column write (a single term's retry) still merges - it is deliberately revising one column.
 */
export function setPaymentGridColumnSources(
    offerType: OfferType,
    columnSources: Record<number, string>,
    options: {
        replace?: boolean;
        refusedTerms?: Record<number, true>;
        pairRejectedTerms?: Record<number, true>;
        keepColumnVerdictsFor?: number[];
    } = {}
) {
    return {
        type: types.SET_PAYMENT_GRID_COLUMN_SOURCES,
        payload: {
            offerType,
            columnSources,
            ...(options.replace && { replace: true }),
            ...(options.refusedTerms && { refusedTerms: options.refusedTerms }),
            ...(options.pairRejectedTerms && { pairRejectedTerms: options.pairRejectedTerms }),
            ...(options.keepColumnVerdictsFor?.length && { keepColumnVerdictsFor: options.keepColumnVerdictsFor })
        }
    };
}

export function setPaymentGridOriginalColumnSources(
    offerType: OfferType,
    {
        remember,
        rememberPrograms,
        replace,
        forget
    }: {
        remember?: Record<number, string>;
        rememberPrograms?: Record<number, GenericObject>;
        replace?: Record<number, string>;
        forget?: number[];
    }
) {
    return {
        type: types.SET_PAYMENT_GRID_ORIGINAL_COLUMN_SOURCES,
        payload: {
            offerType,
            ...(remember && Object.keys(remember).length && { remember }),
            ...(rememberPrograms && Object.keys(rememberPrograms).length && { rememberPrograms }),
            ...(replace && Object.keys(replace).length && { replace }),
            ...(forget?.length && { forget })
        }
    };
}

export function restorePaymentGridTermOverrides(offerType: OfferType, byTerm: Record<number, GenericObject>) {
    return {
        type: types.RESTORE_PAYMENT_GRID_TERM_OVERRIDES,
        payload: { offerType, byTerm }
    };
}

export function clearPaymentGridColumnFigures(offerType: OfferType, termMonths: number) {
    return {
        type: types.CLEAR_PAYMENT_GRID_COLUMN_FIGURES,
        payload: {
            offerType,
            termMonths
        }
    };
}

export function setPaymentGridLeftTerms(offerType: OfferType, leftTermsByTerm: Record<number, number[] | null>) {
    return {
        type: types.SET_PAYMENT_GRID_LEFT_TERMS,
        payload: {
            offerType,
            leftTermsByTerm
        }
    };
}

export function clearPaymentGridTermRecords(offerType: OfferType, termMonths: number) {
    return {
        type: types.CLEAR_PAYMENT_GRID_TERM_RECORDS,
        payload: {
            offerType,
            termMonths
        }
    };
}

export function clearPaymentGridTermPrograms(offerType: OfferType, termMonths: number[]) {
    return {
        type: types.CLEAR_PAYMENT_GRID_TERM_PROGRAMS,
        payload: {
            offerType,
            termMonths
        }
    };
}

/**
 * Records the grid build now in progress - the discovery and every row fanned out from it. `id` is the supersession key:
 * `handleGridActions` acts only on a response belonging to the current build. In Redux rather than a module counter
 * because it is per-deal and survives the middleware being re-created on `INIT_PENCIL_SUCCESS`.
 */
export function setPaymentGridBuild(
    offerType: OfferType,
    build: { id: string; isUserProgramQuotes?: boolean; aspUnticked?: boolean }
) {
    return {
        type: types.SET_PAYMENT_GRID_BUILD,
        payload: {
            offerType,
            build
        }
    };
}

/**
 * Records the program the deal was priced on when the grid opened, so a term edit on the deal's own column still pins
 * it after the selected term has moved to a term with no record yet. Explicit-lender grid only; `RESET_PAYMENT_GRID`
 * clears it on close and on a rebuild.
 */
export function setPaymentGridDealProgram(
    offerType: OfferType,
    dealProgram: GenericObject | undefined,
    calculatorPricedDealTerm?: number
) {
    return {
        type: types.SET_PAYMENT_GRID_DEAL_PROGRAM,
        payload: {
            offerType,
            dealProgram,
            calculatorPricedDealTerm
        }
    };
}

export function setPaymentGridDealTermBeforeEdit(
    offerType: OfferType,
    termId: string | undefined,
    overrides?: Record<number, GenericObject>
) {
    return {
        type: types.SET_PAYMENT_GRID_DEAL_TERM_BEFORE_EDIT,
        payload: {
            offerType,
            termId,
            overrides
        }
    };
}

/**
 * US2041815: records when each grid cell last priced successfully, keyed by term id.
 *
 * The timestamp is the freshness clock a stored grid is judged by, so it has to be the time the payment
 * response landed — not the time of a later save, which would make an old quote look freshly priced.
 */
/**
 * US2085313: puts a stored grid's priced cells back into the terms node.
 *
 * Keyed by term id, which is what the grid renders from: `getPaymentGridItem` reads
 * `getTerm(state, termId)`, so a cell is empty precisely when that record is missing. Restoring here
 * rather than into a grid-only slice is what makes a loaded grid behave like a priced one — selection
 * replays the stored response through the same reducers, and needs no payment call.
 */
export function restorePaymentGridTerms(offerType: OfferType, byId: Record<string, GenericObject>) {
    return {
        type: types.RESTORE_PAYMENT_GRID_TERMS,
        payload: { offerType, byId }
    };
}

export function restorePaymentGridPrograms(offerType: OfferType, termRecords: GenericObject[]) {
    return {
        type: types.RESTORE_PAYMENT_GRID_PROGRAMS,
        payload: { offerType, termRecords }
    };
}

export function setPaymentGridCellPricedAt(offerType: OfferType, cellPricedAt: Record<string, number>) {
    return {
        type: types.SET_PAYMENT_GRID_CELL_PRICED_AT,
        payload: { offerType, cellPricedAt }
    };
}

export function holdPaymentGridForVehicleSwitch(offerType: OfferType) {
    return {
        type: types.HOLD_PAYMENT_GRID_FOR_VEHICLE_SWITCH,
        payload: { offerType }
    };
}

export function rebuildPaymentGridAfterVehicleSwitch(offerType: OfferType) {
    return {
        type: types.REBUILD_PAYMENT_GRID_AFTER_VEHICLE_SWITCH,
        payload: { offerType }
    };
}

export function resetPaymentGrid(offerType: OfferType, dropOverrides: DropOverrides, staleQuoteTerms: number[] = []) {
    return {
        type: types.RESET_PAYMENT_GRID,
        payload: {
            offerType,
            dropOverrides,
            ...(staleQuoteTerms.length ? { staleQuoteTerms } : {})
        }
    };
}

export function paymentGridFetchVppProducts() {
    return {
        type: types.PAYMENT_GRID_FETCH_VPP_PRODUCTS
    };
}

export function updateOfferPriceDebounced(offerPrice: number, options: SuppressOptions = {}) {
    const action: AnyFSA = {
        type: types.UPDATED_OFFER_PRICE_DEBOUNCED,
        payload: offerPrice,
        meta: {
            middleware: {
                payment: true,
                analytics: {
                    eventName: 'DR_OFFER_PRICE_CHANGED'
                }
            }
        }
    };

    if (options?.suppressAnalytics) {
        delete action.meta.middleware.analytics;
    }

    return action;
}

export function updateOfferPriceImmediate(offerPrice: number) {
    return {
        type: types.UPDATED_OFFER_PRICE_IMMEDIATE,
        payload: offerPrice,
        meta: {
            middleware: {
                payment: false
            }
        }
    };
}

export function applyDealerDiscounts(dealerDiscounts: ItemizedDiscount[], payment = true) {
    return {
        type: types.APPLY_DEALER_DISCOUNTS,
        payload: dealerDiscounts,
        meta: {
            middleware: {
                payment
            }
        }
    };
}

export function updateSellRateOverride(
    sellRate: number | undefined,
    months: number,
    {
        isSelectedTerm = true,
        skipPaymentCall = false,
        paymentGridLenderProgram,
        userIntent,
        rateType,
        gridColumnIndex,
        pinDealLevelRateType = false
    }: {
        isSelectedTerm?: boolean;
        skipPaymentCall?: boolean;
        paymentGridLenderProgram?: GenericObject | undefined;
        userIntent?: string;
        /** US2075533: rate type the value was entered under, stored with the value for this term. */
        /**
         * Narrowed to the two units the grid can render and convert: `RateTypeMap` has no entry for
         * `InterestRate` (blank label) and `convertRatesByType` treats anything but MoneyFactor as APR, so
         * storing it would mislabel and mis-convert. Narrowing here makes a caller passing it a compile error
         * rather than a silent hole.
         */
        rateType?: Extract<paymentServicesTypes.RateType, 'APR' | 'MoneyFactor'>;
        gridColumnIndex?: number;
        /**
         * DE766422: also record `rateType` as the deal-level unit while the deal is on program-quotes, which
         * the reducer otherwise does only on user-program-quotes.
         *
         * Claim this only when the unit named above is the unit of the deal as a whole — the lease PQ→UPQ
         * fallback restoring the rate the deal was pricing at. It is not for a caller writing one column:
         * `getRateTypeByTerm` prefers the deal-level slot over every term's own `rateType`, so the write is
         * per-column but the relabel is not, and a money factor in another column would go out declared APR
         * (DE762937's failure with the operands swapped).
         */
        pinDealLevelRateType?: boolean;
    } = {},
    offerType?: OfferType
) {
    return {
        type: types.UPDATED_SELL_RATE_OVERRIDE,
        payload: {
            sellRate,
            months,
            isSelectedTerm,
            program: paymentGridLenderProgram,
            ...(rateType && { rateType }),
            ...(pinDealLevelRateType && { pinDealLevelRateType })
        },
        meta: {
            middleware: {
                payment: !skipPaymentCall,
                analytics: {
                    eventName: 'DR_SELL_RATE_OVERRIDE_CHANGED'
                }
            },
            skipPaymentCall,
            ...(offerType && { selectedOfferType: offerType }),
            ...(userIntent && { userIntent }),
            ...(gridColumnIndex != null && { gridColumnIndex })
        }
    };
}

export function updateSellRateForSelectedTermUPQ(
    sellRateOverride: number | undefined,
    rateType?: Extract<paymentServicesTypes.RateType, 'APR' | 'MoneyFactor'>
) {
    return {
        type: types.UPDATED_SELL_RATE_FOR_SELECTED_TERM_UPQ,
        payload: sellRateOverride,
        ...(rateType && { meta: { rateType } })
    };
}

export function updateFinanceSellRateForSelectedTermUPQ(sellRateOverride: number) {
    return {
        type: types.UPDATED_FINANCE_SELL_RATE_FOR_SELECTED_TERM_UPQ,
        payload: sellRateOverride
    };
}

export function updateBuyRateOverride(
    buyRate: number | undefined,
    months: number,
    {
        isSelectedTerm = true,
        withPayment = true,
        userIntent,
        rateType,
        typedByUser
    }: {
        isSelectedTerm?: boolean;
        withPayment?: boolean;
        userIntent?: string;
        rateType?: Extract<paymentServicesTypes.RateType, 'APR' | 'MoneyFactor'>;
        typedByUser?: boolean;
    } = {}
) {
    return {
        type: types.UPDATED_BUY_RATE_OVERRIDE,
        payload: { buyRate, months, isSelectedTerm, ...(rateType && { rateType }) },
        meta: {
            middleware: {
                payment: withPayment,
                analytics: {
                    eventName: 'DR_BUY_RATE_OVERRIDE_CHANGED'
                }
            },
            ...(userIntent && { userIntent }),
            ...(typedByUser && { typedByUser })
        }
    };
}

export function updateBuyRateForSelectedTermUPQ(
    buyRateOverride: number,
    rateType?: Extract<paymentServicesTypes.RateType, 'APR' | 'MoneyFactor'>
) {
    return {
        type: types.UPDATED_BUY_RATE_FOR_SELECTED_TERM_UPQ,
        payload: buyRateOverride,
        ...(rateType && { meta: { rateType } })
    };
}

export function updateMaxResidualizedMsrpOverride(maxResidualizedMsrp: number, maxResidualizedMsrpApplied: boolean) {
    return {
        type: types.UPDATED_MAX_RESIDUALIZED_MSRP_OVERRIDE,
        payload: { maxResidualizedMsrp, maxResidualizedMsrpApplied },
        meta: {
            middleware: {
                payment: true,
                analytics: {
                    eventName: 'DR_MAX_RESIDUALIZED_MSRP_OVERRIDE_CHANGED'
                }
            }
        }
    };
}

export function updateSecurityDepositAmountOverride(securityDespositAmount: number) {
    return {
        type: types.UPDATED_SECURITY_DEPOSIT_OVERRIDE,
        payload: securityDespositAmount,
        meta: {
            middleware: {
                payment: true,
                analytics: {
                    eventName: 'DR_SECURITY_DEPOSIT_OVERRIDE_CHANGED'
                }
            }
        }
    };
}

export function updateMileageChargeOverride(amount: number) {
    return {
        type: types.UPDATED_MILEAGE_CHARGE_OVERRIDE,
        payload: amount,
        meta: {
            middleware: {
                payment: true,
                analytics: {
                    eventName: 'DR_UPDATED_MILEAGE_CHARGE_OVERRIDE_CHANGED'
                }
            }
        }
    };
}

export function updateIncentivesTotalOverride(amount: number) {
    return {
        type: types.UPDATED_INCENTIVES_TOTAL_OVERRIDE,
        payload: amount,
        meta: {
            middleware: {
                payment: true,
                analytics: {
                    eventName: 'DR_UPDATED_INCENTIVES_TOTAL_OVERRIDE_CHANGED'
                }
            }
        }
    };
}

export function clearReserveSplitLenderChangeMessage() {
    return {
        type: types.CLEARED_RESERVE_SPLIT_LENDER_CHANGE_MESSAGE
    };
}

export function updateReserveSplitOverride(reserveSplit: number | undefined) {
    return {
        type: types.UPDATED_RESERVE_SPLIT_OVERRIDE,
        payload: reserveSplit,
        meta: {
            middleware: {
                payment: true,
                analytics: {
                    eventName: 'DR_RESERVE_SPLIT_OVERRIDE_CHANGED'
                }
            }
        }
    };
}

export function updateAcqFeeOverride(amount: number) {
    return {
        type: types.UPDATED_ACQFEE_OVERRIDE,
        payload: amount,
        meta: {
            middleware: {
                payment: true,
                analytics: {
                    eventName: 'DR_ACQFEE_OVERRIDE_CHANGED'
                }
            }
        }
    };
}

export function updateAcqFeeUpFrontOverride(enabled: boolean) {
    return {
        type: types.UPDATED_ACQFEE_UPFRONT_OVERRIDE,
        payload: enabled,
        meta: {
            middleware: {
                payment: true,
                analytics: {
                    eventName: 'DR_ACQFEE_UPFRONT_OVERRIDE_CHANGED'
                }
            }
        }
    };
}

export function updateVehicleRetailPriceOverride(amount: number) {
    return {
        type: types.UPDATED_RETAIL_PRICE_OVERRIDE,
        payload: amount,
        meta: {
            middleware: {
                payment: true,
                analytics: {
                    eventName: 'DR_RETAIL_PRICE_OVERRIDE_CHANGED'
                }
            }
        }
    };
}

export function updateVehicleInfoDetails(payload: vehicleInfoPayload, payment = false, rebaselineUnsavedChanges = false) {
    return {
        type: types.UPDATED_VEHICLE_INFO_DETAILS,
        payload,
        meta: {
            middleware: {
                payment,
                analytics: {
                    eventName: 'DR_VEHICLE_INFO_DETAILS_CHANGED'
                }
            },
            ...(rebaselineUnsavedChanges && { rebaselineUnsavedChanges: true })
        }
    };
}

export const updateAdjustedResidualOverride = (residualValues: ResidualValues, payment = true) => {
    let actiontype = types.UPDATED_ADJUSTED_RESIDUAL_PERCENT_OVERRIDE;
    let analytics = 'DR_RETAIL_ADJUSTED_RESIDUAL_PERCENT_CHANGED';

    const { value, months, unitType } = residualValues;

    if (unitType === RESIDUAL_TYPE.dollar) {
        actiontype = types.UPDATED_ADJUSTED_RESIDUAL_DOLLAR_OVERRIDE;
        analytics = 'DR_RETAIL_ADJUSTED_RESIDUAL_DOLLAR_CHANGED';
    }

    return {
        type: actiontype,
        payload: { months, value },
        meta: {
            middleware: {
                payment,
                analytics: {
                    eventName: analytics
                }
            }
        }
    };
};

export const updateBaseResidualOverride = (residualValues: ResidualValues, payment = true) => {
    let actiontype = types.UPDATED_BASE_RESIDUAL_PERCENT_OVERRIDE;
    let analytics = 'DR_RETAIL_BASE_RESIDUAL_PERCENT_CHANGED';

    const { value, months, unitType } = residualValues;

    if (unitType === RESIDUAL_TYPE.dollar) {
        actiontype = types.UPDATED_BASE_RESIDUAL_DOLLAR_OVERRIDE;
        analytics = 'DR_RETAIL_BASE_RESIDUAL_DOLLAR_CHANGED';
    }

    return {
        type: actiontype,
        payload: { months, value },
        meta: {
            middleware: {
                payment,
                analytics: {
                    eventName: analytics
                }
            }
        }
    };
};

export const updateProgramResidualOverride = (
    residualValues: ResidualValues,
    payment = true,
    {
        isSelectedTerm = true,
        skipPaymentCall = false,
        syncBaseResidual = false,
        clearBaseResidual = false,
        residualUnitChanged = false,
        userIntent,
        gridColumnIndex
    }: {
        isSelectedTerm?: boolean;
        skipPaymentCall?: boolean;
        /**
         * Also write the base residual from this value. The payment payload prices a lease off
         * `baseResidualValue`, not `programResidualValue`, so a caller that means "this is the residual to
         * price this term with" — the payment grid — has to set both, the way `updateBaseResidualOverride`
         * does. The Residual Breakdown tab's Program Residual field leaves this off, since there the program
         * residual is a field in its own right and the base residual stays independent of it.
         */
        syncBaseResidual?: boolean;
        clearBaseResidual?: boolean;
        /** Came in a unit the column was not already in, so program-quotes cannot honour it - see `paymentGridMiddleware`. */
        residualUnitChanged?: boolean;
        userIntent?: string;
        gridColumnIndex?: number;
    } = {},
    offerType?: OfferType
) => {
    let actiontype = types.UPDATED_PROGRAM_RESIDUAL_PERCENT_OVERRIDE;
    let analytics = 'DR_RETAIL_PROGRAM_RESIDUAL_PERCENT_CHANGED';

    const { value, months, unitType } = residualValues;

    if (unitType === RESIDUAL_TYPE.dollar) {
        actiontype = types.UPDATED_PROGRAM_RESIDUAL_DOLLAR_OVERRIDE;
        analytics = 'DR_RETAIL_PROGRAM_RESIDUAL_DOLLAR_CHANGED';
    }

    return {
        type: actiontype,
        payload: { months, value, isSelectedTerm, syncBaseResidual, clearBaseResidual },
        meta: {
            middleware: {
                payment: payment && !skipPaymentCall,
                analytics: {
                    eventName: analytics
                }
            },
            skipPaymentCall,
            ...(offerType && { selectedOfferType: offerType }),
            ...(userIntent && { userIntent }),
            ...(gridColumnIndex != null && { gridColumnIndex }),
            ...(residualUnitChanged && { residualUnitChanged })
        }
    };
};

/**
 * Takes back the residual a payment grid column was given, which `updateProgramResidualOverride` has no way to
 * express: it writes a value, and every value it accepts is a residual to price with. Blanking the cell used to
 * dispatch nothing, so the override stayed and the cell repopulated from it on the next render (DE762937).
 *
 * No unit is carried. The write chose its keys from the unit the user typed in, and the removal reads back the
 * ones that column actually holds, so the two cannot disagree about which unit is being taken back.
 */
export const removeProgramResidualOverride = (
    months: number,
    payment = true,
    {
        isSelectedTerm = true,
        userIntent,
        gridColumnIndex
    }: { isSelectedTerm?: boolean; userIntent?: string; gridColumnIndex?: number } = {},
    offerType?: OfferType
) => {
    return {
        type: types.REMOVED_PROGRAM_RESIDUAL_OVERRIDE,
        payload: { months, isSelectedTerm },
        meta: {
            middleware: {
                payment
            },
            ...(offerType && { selectedOfferType: offerType }),
            ...(userIntent && { userIntent }),
            ...(gridColumnIndex != null && { gridColumnIndex })
        }
    };
};

/** Points the deal-level residual at the term now selected, in one unit, with the type it is expressed in. */
export function updateResidualForSelectedTermUPQ(
    residualValues: {
        unitType: ResidualValueType;
        adjustedUnitType?: ResidualValueType;
        baseResidualAmount?: number;
        baseResidualPercent?: number;
        programResidual?: number;
        programResidualPercent?: number;
        adjustedResidualAmount?: number;
        adjustedResidualPercent?: number;
    },
    offerType?: OfferType
) {
    return {
        type: types.UPDATED_RESIDUAL_FOR_SELECTED_TERM_UPQ,
        payload: residualValues,
        meta: {
            ...(offerType && { selectedOfferType: offerType })
        }
    };
}

export function selectedCreditTier(id: any, options: SuppressOptions = {}) {
    const action: AnyFSA = {
        type: types.SELECTED_CREDIT_TIER,
        payload: id,
        meta: {
            termStrategy: 'all',
            middleware: {
                analytics: {
                    eventName: 'DR_CREDIT_TIER_CHANGED'
                },
                payment: options?.suppressPayment ? false : true
            }
        }
    };

    if (options?.suppressAnalytics) {
        delete action.meta.middleware.analytics;
    }

    return action;
}

export function updateCreditScoreSource(source: CreditScoreSource) {
    return {
        type: types.UPDATE_CREDIT_SCORE_SOURCE,
        payload: source
    };
}

export function updateManualCreditScore(score: number, options: SuppressOptions = {}) {
    const action: AnyFSA = {
        type: types.UPDATED_MANUAL_CREDIT_SCORE,
        payload: score,
        meta: {
            middleware: {
                analytics: {
                    eventName: 'DR_CREDIT_SCORE_UPDATED_MANUALLY'
                },
                payment: true
            }
        }
    };

    if (options?.suppressAnalytics) {
        delete action.meta.middleware.analytics;
    }

    return action;
}

export function clearManualCreditScore() {
    return {
        type: types.CLEAR_MANUAL_CREDIT_SCORE
    };
}

export function updateCreditAppRefs(creditAppRefs: any) {
    return {
        type: types.CREDIT_APP_SUBMITTED_AWAITING_DECISION,
        payload: creditAppRefs,
        meta: {
            middleware: {
                analytics: {
                    eventName: 'DR_CREDIT_APP_SUBMITTED_AWAITING_DECISION'
                }
            }
        }
    };
}

export function updateCreditAppCoApplicantEmail(creditAppCoApplicantEmail: any) {
    return {
        type: types.CREDIT_APP_CO_APPLICANT_EMAIL,
        payload: creditAppCoApplicantEmail,
        meta: {
            middleware: {
                analytics: {
                    eventName: 'DR_CREDIT_APP_CO_APPLICANT_EMAIL'
                }
            }
        }
    };
}

export function updatedCreditAppZipCode({ month, zipCode }: { month: number; zipCode: IShopper['zip'] }) {
    return {
        type: types.CREDIT_APP_ZIP_CODE_UPDATED,
        meta: {
            month,
            zipCode
        }
    };
}

export function updateDownPaymentDebounced(options: SuppressOptions = {}) {
    const action: AnyFSA = {
        type: types.UPDATED_DOWN_PAYMENT_DEBOUNCED,
        meta: {
            middleware: {
                payment: true,
                analytics: {
                    eventName: 'DR_DOWN_PAYMENT_CHANGED'
                }
            }
        }
    };

    if (options?.suppressAnalytics) {
        delete action.meta.middleware.analytics;
    }

    return action;
}

export function updateDownPaymentImmediate(downPayment: number, offerType?: OfferType) {
    return {
        type: types.UPDATED_DOWN_PAYMENT_IMMEDIATE,
        payload: downPayment,
        meta: {
            middleware: {
                payment: false,
                offerType
            }
        }
    };
}

export function selectedAnnualMiles(miles: number, options: SuppressOptions = {}) {
    const action: AnyFSA = {
        type: types.SELECTED_ANNUAL_MILES,
        payload: miles,
        meta: {
            middleware: {
                analytics: {
                    eventName: 'DR_LEASE_ANNUAL_MILES_CHANGED'
                },
                payment: true
            }
        }
    };

    if (options?.suppressAnalytics) {
        delete action.meta.middleware.analytics;
    }

    return action;
}

/**
 * Update the shopper/buyer address and contact info using data from the customer card PBC.
 *
 * NOTE: This action will not update the shopper's name.
 * @param customer
 * @returns
 */
export function updatedShopperLocationFromConsumer(
    originalShopper: IShopper,
    customer: CommonConsumer,
    allowEmptyValues: boolean = false,
    optionalDesking: boolean = false,
    replace: boolean = false,
    isPostalCodeDigitsOnlyValidationEnabled: boolean = false
) {
    const hasShopperAddressChanged = (original: IShopper, updated: IShopper) => {
        if (updated.streetAddress !== original.streetAddress) return true;
        if (updated.streetAddress2 !== original.streetAddress2) return true;
        if (updated.streetAddress3 !== original.streetAddress3) return true;
        if (updated.city !== original.city) return true;
        if (updated.state !== original.state) return true;
        if (updated.zip !== original.zip) return true;
        if (updated.county !== original.county) return true;

        return false;
    };

    // Program eligibility keys off the zip alone, so the consumers that discard a lender program on this
    // action need it told apart from the six other address fields `payment` already covers. Compared on the
    // five-digit form `combineCommonConsumerContactInfo` writes, and only when a replacement arrived: that
    // helper blanks the zip before re-applying it, so a bare truncation or a blanking is not a move.
    const hasProgramZipChanged = (original: IShopper, updated: IShopper): boolean => {
        const updatedZip = getFirstFiveDigitsPostalCode(updated.zip);
        if (!updatedZip) return false;
        return updatedZip !== getFirstFiveDigitsPostalCode(original.zip);
    };
    const baseShopper = replace ? initialState.shopper : originalShopper;
    const updatedShopper = combineCommonConsumerContactInfo(
        baseShopper,
        customer,
        allowEmptyValues,
        optionalDesking,
        isPostalCodeDigitsOnlyValidationEnabled
    );
    const payment = hasShopperAddressChanged(originalShopper, updatedShopper);

    return {
        type: types.UPDATED_SHOPPER_CONTACT,
        payload: updatedShopper,
        meta: {
            shopperZipChanged: hasProgramZipChanged(originalShopper, updatedShopper),
            replace,
            middleware: {
                payment
            }
        }
    };
}

/**
 * Update retry the shopper/buyer address and contact info using data from the customer card PBC.
 *
 * NOTE: This action will not update the shopper's name.
 */
export function updatedShopperLocationFromConsumerRetry() {
    return {
        type: types.UPDATED_SHOPPER_CONTACT_RETRY,
        meta: {
            middleware: {
                payment: true
            }
        }
    };
}

export function businessConversionCompleted(commonConsumerId: string) {
    return {
        type: types.BUSINESS_CONVERSION_SUCCESS,
        payload: {
            commonConsumerId
        }
    };
}

/**
 * Legacy action creator for changing shopper from SalesView/SP
 * @param shopper { shopper: IShopper, message: string } - Shopper object and message
 * @returns
 */
export function updatedShopper(shopper: { shopper: IShopper; message: string }) {
    return {
        type: types.UPDATED_SHOPPER,
        payload: shopper
    };
}

export function updatedShopperMessage(message: string) {
    return {
        type: types.UPDATED_SHOPPER_MESSAGE,
        payload: { message }
    };
}

export function updateTermAvailableIncentives(incentives: {
    conditionals: { id: number; amount?: number; exclusions?: number[] }[];
    nonConditionals: { id: number; amount?: number; exclusions?: number[] }[];
    incentivesContext?: { programContextId?: string; incentivesInputsKey?: string; compatibleIncentiveIds?: number[] };
    standardsEchoIsAuthoritative?: boolean;
}) {
    return {
        type: types.UPDATE_TERM_AVAILABLE_INCENTIVES,
        payload: incentives
    };
}

// Cash deals have no term, so program-orchestrator incentives can't go through
// updateTermAvailableIncentives. Dispatches a cash-scoped type instead of reusing
// UPDATE_AVAILABLE_INCENTIVES, which incentivesStateUtils.ts/termsStateUtils.ts also handle for
// finance/lease - a cash-only payload there would discard their state.
export function updateCashAvailableIncentives(incentives: {
    conditionals: { id: number; amount?: number }[];
    nonConditionals: { id: number; amount?: number }[];
    incentivesContext?: { compatibleIncentiveIds?: number[] };
}) {
    const { conditionals, nonConditionals, incentivesContext } = incentives;
    return {
        type: types.UPDATE_CASH_AVAILABLE_INCENTIVES,
        payload: { cash: { incentives: { conditionals, nonConditionals }, incentivesContext } }
    };
}

export function selectedConditionalIncentives(
    selectedIncentives: string[],
    preserveManualIncentives = false,
    triggerPayment = true,
    replaceNonConditionalSelection = false
) {
    return {
        type: types.UPDATE_SELECTED_INCENTIVES,
        payload: selectedIncentives,
        meta: {
            preserveManualIncentives,
            replaceNonConditionalSelection,
            middleware: {
                payment: triggerPayment
            }
        }
    };
}

export function locationStandardsReconciled(selectedIncentives: string[], triggerPayment: boolean) {
    return {
        type: types.LOCATION_STANDARDS_RECONCILED,
        payload: selectedIncentives,
        meta: {
            preserveManualIncentives: true,
            replaceNonConditionalSelection: true,
            middleware: {
                payment: triggerPayment
            }
        }
    };
}

export function locationStandardsStillPending(pending: StandardsPendingLocationChange) {
    return {
        type: types.LOCATION_STANDARDS_STILL_PENDING,
        payload: pending
    };
}

export function modifyManualIncentive(incentive: any, action: 'add' | 'remove' | 'edit', triggerPayment = true) {
    return {
        type: types.MODIFY_MANUAL_INCENTIVE,
        payload: {
            incentive,
            action
        },
        meta: {
            middleware: {
                payment: triggerPayment
            }
        }
    };
}

export function selectedConditionalIncentivesWithTerm(selectedIncentives: string[], termId: string) {
    return {
        type: types.APPLIED_PROGRAM,
        payload: { selectedIncentives, termId },
        meta: {
            middleware: {
                payment: true,
                analytics: {
                    eventName: 'DR_PAYMENT_TERM_CHANGED'
                }
            }
        }
    };
}

export function updatedTradeIn(tradeIn: any) {
    return {
        type: types.UPDATED_TRADE_IN,
        payload: tradeIn,
        meta: {
            middleware: {
                payment: true
            }
        }
    };
}

export function updateShopperZip(zip: string, skipPayment = false) {
    return {
        type: types.UPDATED_SHOPPER_ZIP,
        payload: zip,
        meta: {
            middleware: {
                payment: !skipPayment
            }
        }
    };
}

export function updateCoBuyer(
    coBuyer: CommonConsumer,
    allowEmptyValues: boolean = false,
    optionalDesking: boolean = false,
    replace: boolean = false,
    isPostalCodeDigitsOnlyValidationEnabled: boolean = false
) {
    return {
        type: types.UPDATED_COBUYER,
        payload: coBuyer,
        meta: {
            allowEmptyValues,
            optionalDesking,
            replace,
            isPostalCodeDigitsOnlyValidationEnabled
        }
    };
}

export function swappedBuyerCoBuyer(shopper: Partial<IShopper>, coBuyer: CoBuyer) {
    return {
        type: types.SWAPPED_BUYER_COBUYER,
        payload: { shopper, coBuyer },
        meta: {
            middleware: {
                payment: true
            }
        }
    };
}

export function swappedGlobalCustomerIds(globalCustomerId: string) {
    return {
        type: types.SWAPPED_GLOBAL_CUSTOMER_IDS,
        payload: { globalCustomerId }
    };
}

export function clearCoBuyer(): { type: string; payload: any } {
    return {
        type: types.UPDATED_COBUYER,
        payload: null
    };
}

export function updateFinanceOfferPrice(offerPrice: number) {
    return {
        type: types.UPDATED_FINANCE_OFFER_PRICE,
        payload: offerPrice,
        meta: {
            middleware: {
                payment: true
            }
        }
    };
}

export function updateFinanceAndCashOfferPrice(offerPrice: number) {
    return {
        type: types.UPDATED_FINANCE_AND_CASH_OFFER_PRICE,
        payload: offerPrice,
        meta: {
            middleware: {
                payment: true
            }
        }
    };
}

export function updateLeaseAndFinanceOfferPrice(offerPrice: number) {
    return {
        type: types.UPDATED_FINANCE_AND_LEASE_OFFER_PRICE,
        payload: offerPrice,
        meta: {
            middleware: {
                payment: true
            }
        }
    };
}

export function updateLeaseAndFinanceAndCashOfferPrice(offerPrice: number) {
    return {
        type: types.UPDATED_FINANCE_AND_LEASE_AND_CASH_OFFER_PRICE,
        payload: offerPrice,
        meta: {
            middleware: {
                payment: true
            }
        }
    };
}

export function acceptDealReset() {
    return {
        type: types.ACCEPT_DEAL_RESET
    };
}

export function switchVehicle(
    vehicle: IVehicle,
    payment = true,
    options: {
        preserveManualIncentives?: boolean;
        clearFeesOverride?: boolean;
        preserveIncentiveSelection?: boolean;
        includeStandardIncentives?: boolean;
    } = {}
) {
    const {
        preserveManualIncentives = false,
        clearFeesOverride = false,
        preserveIncentiveSelection = false,
        includeStandardIncentives = false
    } = options;
    return {
        type: types.SWITCH_VEHICLE,
        payload: vehicle,
        meta: {
            middleware: {
                payment
            },
            excludeOverrides: true,
            preserveManualIncentives,
            clearFeesOverride,
            preserveIncentiveSelection,
            ...(includeStandardIncentives && { includeStandardIncentives })
        }
    };
}

export function clearVehicleFulfillment() {
    return {
        type: types.CLEAR_VEHICLE_FULFILLMENT
    };
}

export function updatedDealerMessage(cmdMessageToShopper: string) {
    return {
        type: types.UPDATED_DEALER_MESSAGE,
        payload: { cmdMessageToShopper }
    };
}

export function updateMessageToSalesperson(cmdMessageToSalesperson: string) {
    return {
        type: types.UPDATE_MESSAGE_TO_SALESPERSON,
        payload: cmdMessageToSalesperson
    };
}

export function updateCreditDecisionEnabled(enabled: boolean, manualIncentiveFeatureEnabled = false, offerType?: OfferType) {
    return {
        payload: enabled,
        type: types.UPDATED_CREDIT_DECISION_ENABLED,
        meta: {
            excludeOverrides: !enabled ? true : undefined,
            ...(offerType && { offerType }),
            middleware: {
                manualIncentiveFeatureEnabled,
                payment: true,
                analytics: {
                    eventName: 'DR_CREDIT_DECISION_ENABLED'
                }
            }
        }
    };
}
// TODO Delete updateCreditDecisionLenderName after feature toggle enableAppliedLenderInfo has been deleted
export function updateCreditDecisionLenderName(name?: string) {
    return {
        payload: name,
        type: types.UPDATED_CREDIT_DECISION_LENDER_NAME
    };
}
export type LenderChangeOptions = {
    origin?: LenderChangeOrigin;
    isReserveSplitEnabled?: boolean;
};

export const buildLenderChangeMeta = ({ origin, isReserveSplitEnabled }: LenderChangeOptions = {}) =>
    origin || isReserveSplitEnabled
        ? {
              meta: {
                  ...(origin && { lenderChangeOrigin: origin }),
                  ...(isReserveSplitEnabled && { isReserveSplitEnabled })
              }
          }
        : {};

export function updateCreditDecisionLender(lender?: ICreditDecisionLender, options?: LenderChangeOptions) {
    return {
        payload: lender,
        type: types.UPDATED_CREDIT_DECISION_LENDER,
        ...buildLenderChangeMeta(options)
    };
}

export function repriceForContractingLenderChange() {
    return {
        type: types.REPRICED_FOR_CONTRACTING_LENDER_CHANGE,
        meta: {
            middleware: {
                payment: true
            }
        }
    };
}

export function updateSecondPaymentDueDateField(secondPaymentDueDate: string, isPayment?: boolean): AnyFSA {
    return {
        payload: secondPaymentDueDate,
        type: types.UPDATED_SECOND_PAYMENT_DUE_DATE_FIELD,
        meta: {
            middleware: {
                payment: isPayment
            }
        }
    };
}

export function updateCreditDecisionStatus(status: string) {
    return {
        payload: status,
        type: types.UPDATED_CREDIT_DECISION_STATUS
    };
}
export function updatePaymentGridToggle(state: boolean, preInitializedPaymentGrid?: GenericObject, userIntent?: string) {
    return {
        payload: state,
        type: types.UPDATED_PAYMENT_GRID_TOGGLE,
        ...((preInitializedPaymentGrid || userIntent) && {
            meta: {
                ...(preInitializedPaymentGrid && { preInitializedPaymentGrid }),
                ...(userIntent && { userIntent })
            }
        })
    };
}
export function initializePaymentGridFromTemplate(paymentGrid: GenericObject, offerType?: OfferType) {
    return {
        payload: { paymentGrid, offerType } as { paymentGrid: GenericObject; offerType?: OfferType },
        type: types.INITIALIZE_PAYMENT_GRID_FROM_TEMPLATE
    };
}
export function closeLenderChangedBanner() {
    return {
        payload: { isLenderChanged: false },
        type: types.CLOSE_LENDER_CHANGED_BANNER
    };
}

export function creditDecisionDetailsAdded(decisionDetails: any) {
    return {
        payload: decisionDetails,
        type: types.CREDIT_DECISION_DETAILS_RECEIVED
    };
}

export function automatedCreditDecisionApproved(lenderDetails: { lenderId: string; lenderName: string }) {
    return {
        payload: lenderDetails,
        type: types.CREDIT_DECISION_APPROVED
    };
}

export function applyCreditDecisionDetails({ months, sellRate }: { months: number; sellRate: number }) {
    return {
        type: types.APPLY_CREDIT_DECISION_DETAILS,
        meta: {
            middleware: {
                payment: true
            },
            [PAYMENT_REQUEST_OVERRIDE_NODE]: {
                [FINANCE]: { months, sellRate }
            }
        }
    };
}

export function updateCurrentCalculationEndpoint(
    calculationEndpoint: CalculationEndpointType,
    offerType?: OfferType,
    metaOptions?: {
        excludeOverrides?: boolean;
        withPayment?: boolean;
        downPayment?: number | null;
        userIntent?: string;
        analyticsPayload?: Record<string, any>;
        skipGridRebuild?: boolean;
        includeStandardIncentives?: boolean;
    }
) {
    return {
        payload: calculationEndpoint,
        type: types.UPDATE_CURRENT_CALCULATION_ENDPOINT,
        meta: {
            offerType,
            excludeOverrides: !!metaOptions?.excludeOverrides,
            middleware: {
                payment: !!metaOptions?.withPayment
            },
            ...(metaOptions?.skipGridRebuild && { skipGridRebuild: true }),
            [PAYMENT_REQUEST_OVERRIDE_NODE]: {
                ...(metaOptions?.downPayment !== undefined && {
                    [offerType as string]: { downPayment: metaOptions.downPayment }
                })
            },
            ...(metaOptions?.userIntent && { userIntent: metaOptions.userIntent }),
            ...(metaOptions?.analyticsPayload && { analyticsPayload: metaOptions.analyticsPayload }),
            ...(metaOptions?.includeStandardIncentives && { includeStandardIncentives: true })
        }
    };
}

export function setProgramFallbackOverrides() {
    return {
        type: types.SET_PROGRAM_FALLBACK_OVERRIDES
    };
}

export function updateTaxItems(manualTaxItems: paymentServicesTypes.TaxItems, offerType: OfferType, withPayment = false) {
    return {
        type: types.UPDATE_TAX_ITEMS,
        payload: {
            offerType,
            manualTaxItems
        },
        meta: {
            middleware: {
                payment: withPayment
            }
        }
    };
}

export function resetTaxOverrides(offerType: OfferType) {
    return {
        type: types.RESET_TAX_OVERRIDES,
        payload: {
            offerType
        },
        meta: {
            middleware: {
                payment: true
            }
        }
    };
}

/**
 * Created to used primarily by Switch Vehicle functionality, for now it resets incentives and accepted deal flag
 */
export function resetOffer() {
    return {
        type: types.RESET_OFFER
    };
}

export const updateIsTargetOutOfPocket = (checked: boolean, makePaymentCall = false) => {
    return {
        type: types.UPDATE_IS_TARGET_OUT_OF_POCKET,
        payload: checked,
        meta: {
            middleware: {
                payment: makePaymentCall
            }
        }
    };
};

export const initOfferCreditTiers = (payload: InitOfferCreditTiersActionPayload) => {
    return {
        type: types.INIT_OFFER_CREDIT_TIERS,
        payload
    };
};

export const partialResetOverrides = (options: ResetOptions = {}) => {
    return {
        type: PARTIAL_RESET_OVERRIDES,
        meta: {
            retainRatesOverride: options.retainRatesOverride || false,
            previousTermMonth: options.previousTermMonth,
            retainSellRateOverride: options.retainSellRateOverride || false,
            retainAcqFeeOverride: options.retainAcqFeeOverride || false,
            ...(options.retainSecurityDepositOverride && { retainSecurityDepositOverride: true })
        }
    };
};

export const selectedCreditPartialResetOverrides = (options: ResetOptions = {}) => {
    return {
        type: SELECTED_CREDIT_PARTIAL_RESET_OVERRIDES,
        meta: {
            retainRatesOverride: options.retainRatesOverride || false,
            previousTermMonth: options.previousTermMonth,
            retainSellRateOverride: options.retainSellRateOverride || false,
            retainAcqFeeOverride: options.retainAcqFeeOverride || false
        }
    };
};

export const locationResetOverrides = (currentOfferType: OfferType, currentTermMonth: number) => {
    return {
        type: LOCATION_RESET_OVERRIDES,
        meta: {
            currentTermMonth,
            currentOfferType
        }
    };
};

export const honorLeaseMileageChange = (currentOfferType: OfferType, currentTermMonth: number) => {
    return {
        type: HONOR_LEASE_MILEAGE_CHANGE,
        meta: {
            currentOfferType,
            currentTermMonth
        }
    };
};

export const fullResetOverrides = (offerType?: OfferType) => {
    return {
        type: FULL_RESET_OVERRIDES,
        payload: offerType
    };
};

export const changeVehicleUpdateOverrides = (options: { preserveForFallback?: boolean } = {}) => {
    return {
        type: CHANGE_VEHICLE_UPDATE_OVERRIDES,
        meta: {
            forEachDealType: true,
            ...(options.preserveForFallback === true && { preserveForFallback: true })
        }
    };
};

export const restoreOverridesForFallback = () => ({
    type: RESTORE_OVERRIDES_FOR_FALLBACK
});

export const clearOverridesFallbackSnapshot = () => ({
    type: CLEAR_OVERRIDES_FALLBACK_SNAPSHOT
});

/**
 * Captures a fallback snapshot of the current offer type's overrides onto the penciled input node
 * WITHOUT wiping any overrides. Intended for the Manager View vehicle-switch PQ→UPQ fallback, where
 * the existing SWITCH_VEHICLE reset already clears the overrides and we only need to preserve a
 * snapshot to restore if the subsequent payment fails.
 */
export const captureOverridesFallbackSnapshot = () => ({
    type: CAPTURE_OVERRIDES_FALLBACK_SNAPSHOT
});

/**
 * Used in Payment Grid Sales View to generate action for partial restore of overrides
 * @param restoreRecord - record with overrides to restore
 */
export const partialRestoreOverrides = (restoreRecord: PartialRestoreRecord) => {
    return {
        type: PARTIAL_RESTORE_OVERRIDES,
        payload: restoreRecord
    };
};

export const setApplicationName = (applicationName: string) => {
    return {
        type: SET_APPLICATION_NAME,
        payload: applicationName
    };
};

export const setOfferSource = (offerSource: string) => {
    return {
        type: types.SET_OFFER_SOURCE,
        payload: offerSource
    };
};

export const updateDealDeposit = (dealDeposit?: number) => {
    return {
        type: types.UPDATE_DEAL_DEPOSIT,
        payload: dealDeposit
    };
};

export const setOutsideLienholder = (outsideLienholder: OutsideLienHolder) => {
    return {
        type: types.SET_OUTSIDE_LIENHOLDER,
        payload: outsideLienholder,
        meta: {
            middleware: {
                payment: outsideLienholder.payment
            }
        }
    };
};

export const setTransferRegistrationDetails = (transferRegistrationDetails: IPaymentOverride, makePaymentCall = false) => {
    return {
        type: types.SET_TRANSFER_REGISTRATION_DETAILS,
        payload: transferRegistrationDetails,
        meta: {
            middleware: {
                payment: makePaymentCall
            }
        }
    };
};

export const setPriorLeaseBalance = (priorLeaseBalance: PriorLeaseBalance, makePaymentCall = true) => {
    return {
        type: types.SET_PRIOR_LEASE_BALANCE,
        payload: priorLeaseBalance,
        meta: {
            middleware: {
                payment: makePaymentCall
            }
        }
    };
};

export const setPreviousFinanceInputWithoutFFB = () => {
    return {
        type: types.SET_PREVIOUS_FINANCE_INPUT_WITHOUT_FFB
    };
};

export const resetPreviousFinanceInputWithoutFFB = () => {
    return {
        type: types.RESET_PREVIOUS_FINANCE_INPUT_WITHOUT_FFB
    };
};

export const updateProfitabilityData = (
    offerType: OfferType,
    termId: string | null,
    profitabilityData: paymentServicesTypes.ProgramQuoteOutput
) => {
    return {
        type: types.UPDATE_PROFITABILITY_DATA,
        payload: {
            offerType,
            termId,
            profitabilityData
        },
        meta: {
            middleware: {
                payment: false
            }
        }
    };
};

export const setLockPaymentInputsInSV = (dealType: typeof FINANCE | typeof LEASE, locked: boolean) => {
    if (dealType !== FINANCE && dealType !== LEASE) {
        return { type: '@@NOOP' };
    }

    const dealTypeKey = dealType.toLowerCase() as 'finance' | 'lease';

    return {
        type: types.SET_LOCK_PAYMENT_INPUTS_IN_SV,
        payload: {
            [dealTypeKey]: locked
        },
        meta: {
            middleware: {
                payment: false
            }
        }
    };
};

export const updateBalloonPaymentAmount = (amount: number, makePaymentCall = true) => {
    return {
        type: types.UPDATED_BALLOON_PAYMENT_AMOUNT,
        payload: amount,
        meta: {
            middleware: {
                payment: makePaymentCall
            }
        }
    };
};

export const updateBalloonType = (balloonType: string, makePaymentCall = true) => {
    return {
        type: types.UPDATED_BALLOON_TYPE,
        payload: balloonType,
        meta: {
            middleware: {
                payment: makePaymentCall
            }
        }
    };
};

export const updateResidualTabDisplayType = (residualType: ResidualValueType, payment = true) => ({
    type: types.UPDATE_RESIDUAL_TAB_DISPLAY_TYPE,
    payload: residualType,
    meta: {
        middleware: {
            payment
        }
    }
});
